import { useState, useEffect, useRef } from "react";
import userFilter from "../../../hooks/filter/useFilter";
import "./FilterSearch.scss";
import { useSearchParams } from "react-router-dom";

export const FilterSearch = ({
  source,
  className
}: {
  source: "filters" | "header",
  className?: string
}) => {
  const { setFilter, filterState } = userFilter();
  const [searchParams] = useSearchParams();
  const [inputValue, setInputValue] = useState("");
  const [prevInputValue , setPrevInputValue] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dynamicPadding, setDynamicPadding] = useState(12);

  useEffect(() => {
    const search: null | string = searchParams.get("search");
    if (search) {
      const searchTags = search.split(',').filter(tag => tag.trim() !== '');
      setTags(searchTags);
      setInputValue("");
    }
  }, [])

  // Actualizar tags cuando cambie filterState.search (ej. desde URL)
  useEffect(() => {
    if (filterState.search && Array.isArray(filterState.search)) {
      // Si el cambio viene de afuera (no coincide con nuestra combinación de tags + input)
      // o si es la carga inicial, actualizamos los tags.
      const currentSearchStrings = filterState.search.map(s => String(s));
      const myCombination = [...tags];
      if (inputValue.trim()) myCombination.push(inputValue.trim());

      const isSame = currentSearchStrings.length === myCombination.length &&
                     currentSearchStrings.every((v, i) => v === myCombination[i]);

      if (!isSame) {
        // Si hay un input activo, el último término del filterState podría ser ese input
        // pero para simplificar, si viene de afuera, asumimos que todos son tags
        // a menos que estemos en medio de una edición.
        if (inputValue === "") {
          setTags(currentSearchStrings);
        }
      }
    } else if (!filterState.search && tags.length > 0) {
      setTags([]);
    }
  }, [filterState.search])

  // Calcular padding dinámico SIMPLE - solo después de las tags
  useEffect(() => {
    if (tags.length === 0) {
      setDynamicPadding(12);
    } else {
      setTimeout(() => {
        const tagsContainer = document.querySelector('.tags-inline');
        if (tagsContainer) {
          const containerWidth = tagsContainer.getBoundingClientRect().width;
          // Solo 15px después de las tags para que no se solape
          setDynamicPadding(containerWidth + 15);
        }
      }, 50);
    }
  }, [tags])

  const delayValue = 400; // Aumentamos un poco para que sea más natural
  const timer = useRef<any>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    }
  }, []);

  const updateGlobalFilter = (newTags: string[], currentInput: string) => {
    const newSearch = [...newTags];
    const trimmedInput = currentInput.trim();
    if (trimmedInput.length > 1) {
      newSearch.push(trimmedInput);
    }

    // Comparar con el estado actual para evitar búsquedas duplicadas
    const currentGlobalSearch = filterState.search || [];
    const isSame = Array.isArray(currentGlobalSearch) &&
                   currentGlobalSearch.length === newSearch.length &&
                   newSearch.every((v, i) => v === currentGlobalSearch[i]);

    if (!isSame) {
      setFilter({ search: newSearch.length > 0 ? newSearch : undefined });
    }
  };

  const addTag = (tagText: string) => {
    const trimmedTag = tagText.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      const newTags = [...tags, trimmedTag];
      setTags(newTags);
      setInputValue("");
      // Al agregar tag, actualizamos globalmente (ahora el input está vacío)
      updateGlobalFilter(newTags, "");
    } else if (trimmedTag && tags.includes(trimmedTag)) {
      // Si ya existe como tag, solo limpiamos el input
      setInputValue("");
      updateGlobalFilter(tags, "");
    }
  };

  const removeTag = (tagToRemove: string) => {
    const newTags = tags.filter(tag => tag !== tagToRemove);
    setTags(newTags);
    updateGlobalFilter(newTags, inputValue);
  };

  const searchNameInput = (ev: any) => {
    if (timer.current) clearTimeout(timer.current);
    
    const value = ev.target.value;

    // Solo agregar etiqueta al presionar Enter
    if (ev.key === 'Enter') {
      ev.preventDefault();
      addTag(value);
      return;
    }

    timer.current = setTimeout(() => {
      updateGlobalFilter(tags, value);
    }, delayValue);
  }

  const keyDownHandle = (ev: any) => {
    // No limpiamos el timer aquí para permitir que el debounce siga su curso
    // a menos que sea una tecla especial.

    // Backspace con input vacío elimina la última etiqueta y la pone en el input para editar
    if (ev.key === 'Backspace' && inputValue.length === 0 && tags.length > 0) {
      ev.preventDefault();
      if (timer.current) clearTimeout(timer.current);
      
      const lastTag = tags[tags.length - 1];
      const newTags = tags.slice(0, -1);
      
      setTags(newTags);
      setInputValue(lastTag);
      
      // Actualizamos el filtro global inmediatamente para que no haya salto
      updateGlobalFilter(newTags, lastTag);
    }
  }

  const handleDropdownToggle = () => {
    if (tags.length > 0) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const getTagClasses = () => {
    return tags.length > 0 ? "has-tags" : "";
  };

  return (
    <div className={`input-group ${source === "header" ? "search-header" : "search-filters"} ${getTagClasses()} ${className ?? ""}`}>
      <div className="input-with-tags">
        {/* Tags visibles en línea */}
        {tags.length > 0 && (
          <div className="tags-inline" onClick={handleDropdownToggle}>
            {/* Primer tag visible */}
            <span className="tag">
              <span className="tag-text">{tags[0]}</span>
              <button 
                type="button" 
                className="tag-remove" 
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tags[0]);
                }}
                aria-label={`Remover tag ${tags[0]}`}
              >
                ×
              </button>
            </span>
            
            {/* Contador si hay más tags */}
            {tags.length > 1 && (
              <span className="tag tag-counter">
                {tags.length - 1} más
              </span>
            )}
          </div>
        )}
        
        <input 
          type="text" 
          className="form-control bg-sm-light" 
          style={{ textIndent: `${dynamicPadding}px` }}
          placeholder={tags.length > 0 ? "" : "Buscar por nombre de proyecto o zona"} 
          aria-label="Username" 
          aria-describedby="basic-addon1" 
          onKeyUp={searchNameInput} 
          onChange={(ev) =>{
            setPrevInputValue(inputValue)
            setInputValue(ev.target.value)
          }}
          onKeyDown={keyDownHandle} 
          value={inputValue}
          onFocus={() => setIsDropdownOpen(false)}
        />
        
        {/* Dropdown con todos los tags */}
        {isDropdownOpen && tags.length > 0 && (
          <div className="tags-dropdown">
            {tags.map((tag, index) => (
              <div key={index} className="dropdown-tag">
                <span className="tag-text">{tag}</span>
                <button 
                  type="button" 
                  className="tag-remove" 
                  onClick={(e) => {
                    e.stopPropagation();
                    removeTag(tag);
                  }}
                  aria-label={`Remover tag ${tag}`}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <span 
        className="input-group-text bg-sm-light" 
        onClick={() => addTag(inputValue)}
        style={{ cursor: 'pointer' }}
      >
        <i className="bi bi-search"></i>
      </span>
    </div>
  );
};